/**
 * File type selector component for upload dialog
 */

import { createElement } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { getFileIcon } from '@/lib/utils/document-utils';
import { FileTypeSlug } from '@/lib/services/files/file-types';

export interface FileTypeSelectorProps {
  value: string;
  onChange: (value: string) => void;
  fileId: string;
  disabled?: boolean;
  excludeCrucial?: boolean; // Exclude death certificate and will
  onlyCrucial?: boolean; // Only show death certificate and will
}

export function FileTypeSelector({
  value,
  onChange,
  fileId,
  disabled,
  excludeCrucial = false,
  onlyCrucial = false
}: FileTypeSelectorProps) {
  // Define document types using backend FileTypeSlug values
  // IMPORTANT: Only use file types that are valid for matter documents
  // Never use CORRESPONDENCE_ATTACHMENT as it requires attachmentId
  const documentTypes = [
    {
      value: FileTypeSlug.DEATH_CERTIFICATE,
      label: 'Death Certificate',
      icon: 'Heart',
      crucial: true
    },
    {
      value: FileTypeSlug.WILL,
      label: 'Will',
      icon: 'ScrollText',
      crucial: true
    },
    {
      value: FileTypeSlug.ADDITIONAL_DOCUMENT,
      label: 'Additional Document',
      icon: 'FileText',
      crucial: false
    },
    {
      value: FileTypeSlug.GRANT_OF_representation,
      label: 'Grant of Representation',
      icon: 'Briefcase',
      crucial: false
    },
    {
      value: FileTypeSlug.PRIMARY_ID,
      label: 'Primary ID',
      icon: 'CreditCard',
      crucial: false
    },
    {
      value: FileTypeSlug.SECONDARY_ID,
      label: 'Secondary ID',
      icon: 'CreditCard',
      crucial: false
    },
    {
      value: FileTypeSlug.INSTITUTION_FORM_SIGNED,
      label: 'Signed Form',
      icon: 'FileText',
      crucial: false
    }
  ];

  // Filter options based on props
  let filteredTypes = documentTypes;
  if (onlyCrucial) {
    filteredTypes = documentTypes.filter((type) => type.crucial);
  } else if (excludeCrucial) {
    filteredTypes = documentTypes.filter((type) => !type.crucial);
  }

  return (
    <div className="space-y-2">
      <Label htmlFor={`file-type-${fileId}`}>Document Type</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger id={`file-type-${fileId}`}>
          <SelectValue placeholder="Select document type" />
        </SelectTrigger>
        <SelectContent>
          {filteredTypes.map((type) => {
            const iconComponent = getFileIcon(type.value);
            return (
              <SelectItem key={type.value} value={type.value}>
                <div className="flex items-center gap-2">
                  {createElement(iconComponent, { className: 'h-4 w-4' })}
                  <span>{type.label}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
